import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { toast } from "sonner";
import { Plus, X, Users } from "@phosphor-icons/react";
import { timingSearchPlayers, timingCompare } from "../../lib/timing-api";
import { t, PERIODS } from "../../lib/timing-i18n";

// Compare 2-4 players — overlay their season-average curves.
export default function CompareView({ lang }) {
    const [players, setPlayers] = useState([]); // [{player_key, display_name}]
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [compareData, setCompareData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [xMode, setXMode] = useState("gw"); // "gw" | "phase"

    useEffect(() => {
        const h = setTimeout(async () => {
            if (!query) {
                setResults([]);
                return;
            }
            try {
                const r = await timingSearchPlayers({ q: query, limit: 15 });
                setResults((r.players || []).filter((p) => !players.find((x) => x.player_key === p.player_key)));
            } catch (e) {
                console.error(e);
            }
        }, 200);
        return () => clearTimeout(h);
    }, [query, players]);

    // Load compare data when the selection is 2..4
    useEffect(() => {
        if (players.length < 2) {
            setCompareData(null);
            return;
        }
        setLoading(true);
        timingCompare(players.map((p) => p.player_key))
            .then((d) => setCompareData(d))
            .catch((e) => toast.error(e?.response?.data?.detail || "Compare failed"))
            .finally(() => setLoading(false));
    }, [players]);

    const addPlayer = (p) => {
        if (players.length >= 4) {
            toast.error(t("compareIntro", lang));
            return;
        }
        setPlayers((cur) => [...cur, { player_key: p.player_key, display_name: p.display_name }]);
        setQuery("");
        setResults([]);
    };

    const removePlayer = (pk) =>
        setPlayers((cur) => cur.filter((x) => x.player_key !== pk));

    const chartData = useMemo(() => {
        if (!compareData?.players?.length) return [];
        if (xMode === "phase") {
            return PERIODS.map(({ labelKey, gw: [lo, hi] }) => {
                const row = { phase: t(labelKey, lang) };
                for (const p of compareData.players) {
                    const pts = (p.average_curve || [])
                        .slice(lo - 1, hi)
                        .map((c) => c?.avg_points)
                        .filter((v) => v != null);
                    row[p.meta.display_name] = pts.length
                        ? Math.round((pts.reduce((a, b) => a + b, 0) / pts.length) * 100) / 100
                        : null;
                }
                return row;
            });
        }
        return Array.from({ length: 38 }, (_, i) => {
            const gw = i + 1;
            const row = { gw };
            for (const p of compareData.players) {
                const avgPt = p.average_curve?.[i];
                row[p.meta.display_name] = avgPt?.avg_points ?? null;
            }
            return row;
        });
    }, [compareData, xMode, lang]);

    const color = (i) => ["#FFF3C2", "#9EE8B4", "#9DB7F5", "#F58BC0"][i % 4];

    return (
        <div className="space-y-4" data-testid="timing-compare">
            <header className="border border-white/10 bg-[#0d0d0d] p-4">
                <div className="flex items-center gap-2 mb-2">
                    <Users size={14} weight="fill" className="text-fa-yellow" />
                    <h2 className="font-display font-black text-xl uppercase tracking-tight">
                        {t("tabCompare", lang)}
                    </h2>
                </div>
                <p className="font-mono text-[11px] text-white/60">{t("compareIntro", lang)}</p>

                {/* Selected chips */}
                <div className="flex gap-2 flex-wrap mt-3">
                    {players.map((p, i) => (
                        <div
                            key={p.player_key}
                            className="flex items-center gap-2 border px-2 py-1 bg-black/40"
                            style={{ borderColor: color(i) }}
                            data-testid={`compare-chip-${i}`}
                        >
                            <span className="w-2 h-2 rounded-full" style={{ background: color(i) }} />
                            <Link to={`/players/${encodeURIComponent(p.player_key)}`}
                                  data-testid={`compare-chip-profile-${i}`}
                                  className="font-display font-black text-sm hover:text-fa-mint transition-colors">{p.display_name}</Link>
                            <button
                                type="button"
                                onClick={() => removePlayer(p.player_key)}
                                data-testid={`compare-remove-${i}`}
                                className="text-white/40 hover:text-red-300"
                                aria-label={t("remove", lang)}
                            >
                                <X size={12} weight="bold" />
                            </button>
                        </div>
                    ))}
                </div>

                {/* Search to add */}
                {players.length < 4 && (
                    <div className="mt-3 relative">
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t("addPlayer", lang) + " — " + t("searchPlaceholder", lang)}
                            data-testid="compare-search"
                            className="w-full bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] focus:outline-none focus:border-fa-yellow"
                        />
                        {results.length > 0 && (
                            <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto border border-white/15 bg-[#0d0d0d] shadow-xl">
                                {results.map((p) => (
                                    <button
                                        key={p.player_key}
                                        type="button"
                                        onClick={() => addPlayer(p)}
                                        data-testid={`compare-add-${p.player_key.replace(/[^a-z0-9]+/gi, "-")}`}
                                        className="w-full text-left px-3 py-1.5 hover:bg-fa-yellow/10 border-b border-white/5 last:border-0"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-display font-black text-sm">{p.display_name}</span>
                                            <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">
                                                {(p.positions || []).join(", ")}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </header>

            {loading && (
                <div className="font-mono text-[11px] text-white/50 italic">{t("loading", lang)}</div>
            )}

            {compareData && (
                <>
                    {/* Chart */}
                    <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="compare-chart">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="font-display font-black text-sm uppercase tracking-widest">
                                {t("averageCurve", lang)} — {t("ppg", lang)} / {xMode === "gw" ? "GW" : (lang === "cs" ? "fáze" : "phase")}
                            </h3>
                            <div className="ml-auto flex items-center gap-1" data-testid="compare-xmode-toggle">
                                {[["gw", "GW"], ["phase", lang === "cs" ? "Fáze" : "Phases"]].map(([k, label]) => (
                                    <button
                                        key={k}
                                        type="button"
                                        onClick={() => setXMode(k)}
                                        data-testid={`compare-xmode-${k}`}
                                        className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${xMode === k ? "bg-fa-yellow text-black border-fa-yellow font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="h-[320px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                                    <XAxis dataKey={xMode === "gw" ? "gw" : "phase"} stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 10 }} />
                                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 10 }} />
                                    <Tooltip
                                        wrapperStyle={{ outline: "none" }}
                                        contentStyle={{
                                            background: "#0d0d0d",
                                            border: "1px solid rgba(255,255,255,0.15)",
                                            fontFamily: "IBM Plex Mono, monospace",
                                            fontSize: 11,
                                            color: "#fff",
                                        }}
                                        labelStyle={{ color: "#FFF3C2", fontWeight: 700 }}
                                        itemStyle={{ color: "#fff" }}
                                        cursor={{ stroke: "rgba(255,255,255,0.25)", strokeWidth: 1 }}
                                        labelFormatter={(v) => xMode === "gw" ? `GW ${v}` : v}
                                    />
                                    <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, textTransform: "uppercase" }} />
                                    {compareData.players.map((p, i) => (
                                        <Line
                                            key={p.player_key}
                                            type="monotone"
                                            dataKey={p.meta.display_name}
                                            stroke={color(i)}
                                            strokeWidth={2}
                                            dot={{ r: xMode === "gw" ? 2 : 4 }}
                                            connectNulls
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Metrics table */}
                    <div className="border border-white/10 bg-[#0d0d0d] p-3 overflow-x-auto" data-testid="compare-metrics">
                        <table className="w-full min-w-[600px]">
                            <thead>
                                <tr className="border-b border-white/10">
                                    <th className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50 p-2">
                                        {t("season", lang)} → {t("totalPoints", lang)}
                                    </th>
                                    {compareData.players.map((p, i) => (
                                        <th
                                            key={p.player_key}
                                            className="text-center font-display font-black text-sm p-2"
                                            style={{ color: color(i) }}
                                        >
                                            {p.meta.display_name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {["total_points", "consistency", "explosiveness", "value"].map((metric) => (
                                    <tr key={metric} className="border-b border-white/5">
                                        <td className="p-2 font-mono text-[10px] uppercase tracking-widest text-white/70">
                                            {t({
                                                total_points: "totalPoints",
                                                consistency: "consistency",
                                                explosiveness: "explosiveness",
                                                value: "value",
                                            }[metric], lang)} · {t("avg", lang)}
                                        </td>
                                        {compareData.players.map((p) => (
                                            <td
                                                key={p.player_key}
                                                className="p-2 text-center font-mono text-[11px] tabular-nums"
                                                data-testid={`compare-cell-${p.player_key.replace(/[^a-z0-9]+/gi, "-")}-${metric}`}
                                            >
                                                {p.aggregate[metric] ?? "—"}
                                                {metric === "explosiveness" && p.aggregate[metric] != null ? "%" : ""}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {!compareData && players.length < 2 && (
                <div className="border-2 border-dashed border-white/15 bg-[#0d0d0d] p-6 text-center">
                    <Plus size={24} weight="bold" className="mx-auto text-white/25 mb-2" />
                    <p className="font-mono text-[11px] text-white/50">{t("compareIntro", lang)}</p>
                </div>
            )}
        </div>
    );
}
